import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, User, Mail, Phone, Shield, Tag } from "lucide-react";
import { load } from "@cashfreepayments/cashfree-js";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function BookPurchase() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  useEffect(() => {
    axios.get(`${API}/books/${id}`)
      .then(r => setBook(r.data))
      .catch(() => navigate("/"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    
    setSubmitting(true);
    setError("");

    try {
      // Create order - include origin_url for proper payment return redirect
      const orderRes = await axios.post(`${API}/books/purchase/create-order`, {
        book_id: id,
        buyer_name: form.name,
        buyer_email: form.email,
        buyer_phone: form.phone,
        origin_url: window.location.origin  // Send the current domain for payment return
      });

      const { order_id, payment_session_id } = orderRes.data;

      // Initialize Cashfree
      const cashfree = await load({ mode: "production" });
      
      // Open payment checkout
      cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: "_self"
      });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to initiate payment. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!book) return null;

  const discountedPrice = book.discount_percentage > 0 
    ? Math.round(book.price * (1 - book.discount_percentage / 100))
    : book.price;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-dm-sans">Back</span>
          </button>
        </div>
      </header>

      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Book Details */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-[#111] border border-zinc-800 overflow-hidden"
            >
              {/* Book Cover */}
              <div className="relative h-64 bg-gradient-to-br from-[#00F0FF]/10 to-[#7000FF]/10">
                <img 
                  src={`${API}/books/${id}/cover`}
                  alt={book.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                {book.discount_percentage > 0 && (
                  <div className="absolute top-3 left-3 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold px-3 py-1 flex items-center gap-1">
                    <Tag size={14} />
                    {book.discount_percentage}% OFF
                  </div>
                )}
              </div>
              
              <div className="p-6">
                <h1 className="font-outfit font-bold text-white text-2xl mb-2">{book.title}</h1>
                <p className="text-[#00F0FF] text-sm font-dm-sans mb-4">by {book.author}</p>
                <p className="text-zinc-400 text-sm font-dm-sans mb-6">{book.description}</p>
                
                {/* Price */}
                <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm font-dm-sans">Price</span>
                    <div className="flex items-center gap-3">
                      {book.discount_percentage > 0 && (
                        <span className="text-zinc-500 line-through text-lg">₹{book.price}</span>
                      )}
                      <span className="text-[#00F0FF] text-3xl font-outfit font-bold">₹{discountedPrice}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Purchase Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-[#111] border border-zinc-800 p-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#00F0FF]/10 rounded-lg flex items-center justify-center">
                  <BookOpen size={20} className="text-[#00F0FF]" />
                </div>
                <div>
                  <h2 className="font-outfit font-bold text-white text-lg">Complete Your Purchase</h2>
                  <p className="text-zinc-500 text-sm font-dm-sans">Enter your details to proceed</p>
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 mb-6 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-zinc-400 text-sm mb-2 font-dm-sans">
                    <User size={14} /> Full Name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-3 text-sm font-dm-sans focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-colors"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-zinc-400 text-sm mb-2 font-dm-sans">
                    <Mail size={14} /> Email Address
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-3 text-sm font-dm-sans focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-colors"
                    placeholder="your@email.com"
                    required
                  />
                  <p className="text-zinc-600 text-xs mt-1">Download link will be sent to this email</p>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-zinc-400 text-sm mb-2 font-dm-sans">
                    <Phone size={14} /> Phone Number
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({...form, phone: e.target.value})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-3 text-sm font-dm-sans focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-colors"
                    placeholder="9876543210"
                    pattern="[0-9]{10}"
                    required
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-white font-bold py-4 text-lg hover:opacity-90 transition-opacity disabled:opacity-50 font-outfit flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>Pay ₹{discountedPrice}</>
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-center gap-2 text-zinc-500 text-xs pt-2">
                  <Shield size={12} />
                  <span>Secure payment powered by Cashfree</span>
                </div>
              </form>

              {/* Benefits */}
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <h3 className="text-zinc-400 text-sm font-bold mb-3">What you'll get:</h3>
                <ul className="space-y-2 text-sm text-zinc-500">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#00F0FF] rounded-full" />
                    Instant PDF download after payment
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#00F0FF] rounded-full" />
                    Download link valid for 7 days
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#00F0FF] rounded-full" />
                    Up to 5 downloads allowed
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#00F0FF] rounded-full" />
                    Email support included
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
